import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
// ─── Airtable Config ────────────────────────────────────────────────────────
const AIRTABLE_BASE_ID = 'app3KTmtifhOKRdfp';
const AIRTABLE_TABLE_ID = 'tblXbn3GcfST1st8e';
const PARTICIPANTS_TABLE_ID = 'tbl0hlwJfalRDPZYD';
const AIRTABLE_API_KEY = 'patIMX7KCCwIe1ZY3.6532f619701577b574ba3f0ec5bad30cfd0cfd8da4c1d587ff176a599eb600c4';
const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/axedc43hmni1ua0ny8q73ca5khzuk0w5';
// ─── Types ───────────────────────────────────────────────────────────────────
interface Workshop {
  id: string;
  name: string;
  session: string;
  status: string;
  isFull: boolean;
}
interface FormState {
  fullName: string;
  email: string;
  phone: string;
  morningWorkshopId: string;
  morningWorkshopName: string;
  afternoonWorkshopId: string;
  afternoonWorkshopName: string;
}
type EmailStatus = 'idle' | 'checking' | 'valid' | 'invalid';
// ─── Confetti Component ───────────────────────────────────────────────────────
function Confetti() {
  const colors = ['#00d4ff', '#0099cc', '#ffffff', '#00ffcc', '#4fc3f7', '#b3e5fc', '#00bcd4', '#80deea'];
  const pieces = Array.from({
    length: 80
  }, (_, i) => ({
    id: i,
    color: colors[i % colors.length],
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    duration: `${2.5 + Math.random() * 2}s`,
    size: `${6 + Math.random() * 8}px`,
    rotation: `${Math.random() * 360}deg`
  }));
  return <div className="fixed inset-0 pointer-events-none overflow-hidden z-50" data-id="element-3021" data-mp="8w19np5d85qj">
      {pieces.map(p => <div key={p.id} style={{
      position: 'absolute',
      left: p.left,
      top: '-20px',
      width: p.size,
      height: p.size,
      backgroundColor: p.color,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      transform: `rotate(${p.rotation})`,
      animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`
    }} data-id="element-3022" data-mp="41ngzui38cpk" />)}
      <style data-id="element-3023" data-mp="1pdyof9jra0rv">{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>;
}
// ─── Wave SVG ─────────────────────────────────────────────────────────────────
function WaveDecoration() {
  return <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{
    height: '60px',
    display: 'block'
  }} data-id="element-3024" data-mp="24foqzat8pfft">
      <path d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" fill="rgba(0,212,255,0.08)" data-id="element-3025" data-mp="2s4zvn3dpntz8" />
      <path d="M0,55 C360,20 720,70 1080,30 C1260,10 1380,50 1440,55 L1440,80 L0,80 Z" fill="rgba(0,212,255,0.05)" data-id="element-3026" data-mp="2w5js04qgmtkb" />
    </svg>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function WorkshopRegistrationPage() {
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loadingWorkshops, setLoadingWorkshops] = useState(true);
  const [workshopError, setWorkshopError] = useState(false);
  const [form, setForm] = useState<FormState>({
    fullName: '',
    email: '',
    phone: '',
    morningWorkshopId: '',
    morningWorkshopName: '',
    afternoonWorkshopId: '',
    afternoonWorkshopName: ''
  });
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [emailErrorMsg, setEmailErrorMsg] = useState('');
  const [participantRecordId, setParticipantRecordId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // ── Fetch workshops on mount ──────────────────────────────────────────────
  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const res = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_ID}`, {
          headers: {
            Authorization: `Bearer ${AIRTABLE_API_KEY}`
          }
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        const mapped: Workshop[] = data.records.map((rec: any) => ({
          id: rec.id,
          name: rec.fields['Workshop Name'] || '',
          session: rec.fields['Session'] || '',
          status: rec.fields['Status'] || '',
          isFull: (rec.fields['Status'] || '').startsWith('FULL')
        }));
        setWorkshops(mapped);
      } catch {
        setWorkshopError(true);
      } finally {
        setLoadingWorkshops(false);
      }
    };
    fetchWorkshops();
  }, []);
  // ── Email validation on blur ──────────────────────────────────────────────
  const handleEmailBlur = useCallback(async () => {
    const email = form.email.trim();
    if (!email) return;
    setEmailStatus('checking');
    setEmailErrorMsg('');
    try {
      const res = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${PARTICIPANTS_TABLE_ID}?filterByFormula={Email}="${encodeURIComponent(email)}"`, {
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`
        }
      });
      const data = await res.json();
      if (data.records && data.records.length > 0) {
        setEmailStatus('valid');
        setParticipantRecordId(data.records[0].id);
      } else {
        setEmailStatus('invalid');
        setEmailErrorMsg('האימייל שלך לא נמצא במערכת. רק רוכשי כרטיס יכולים להירשם לסדנאות.');
        setParticipantRecordId(null);
      }
    } catch {
      setEmailStatus('invalid');
      setEmailErrorMsg('שגיאה בבדיקת האימייל. אנא נסו שוב.');
    }
  }, [form.email]);
  // ── Handle field changes ──────────────────────────────────────────────────
  const handleChange = (field: keyof FormState, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'email') {
      setEmailStatus('idle');
      setEmailErrorMsg('');
    }
  };
  const handleWorkshopSelect = (session: 'morning' | 'afternoon', recordId: string) => {
    const workshop = workshops.find(w => w.id === recordId);
    if (session === 'morning') {
      setForm(prev => ({
        ...prev,
        morningWorkshopId: recordId,
        morningWorkshopName: workshop?.name || ''
      }));
    } else {
      setForm(prev => ({
        ...prev,
        afternoonWorkshopId: recordId,
        afternoonWorkshopName: workshop?.name || ''
      }));
    }
  };
  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const payload = {
        formId: 'suprace-custom-form',
        formName: 'הרשמה לסדנאות',
        submission: {
          submissionTime: new Date().toISOString(),
          questions: [{
            id: 'fullName',
            name: 'שם מלא',
            type: 'ShortAnswer',
            value: form.fullName
          }, {
            id: 'email',
            name: 'אימייל',
            type: 'EmailInput',
            value: form.email
          }, {
            id: 'phone',
            name: 'טלפון',
            type: 'PhoneNumber',
            value: form.phone
          }, {
            id: 'morningWorkshop',
            name: 'סדנת בוקר',
            type: 'RecordPicker',
            value: [{
              recordID: form.morningWorkshopId,
              'Workshop Name': form.morningWorkshopName
            }]
          }, {
            id: 'afternoonWorkshop',
            name: 'סדנת צהריים',
            type: 'RecordPicker',
            value: [{
              recordID: form.afternoonWorkshopId,
              'Workshop Name': form.afternoonWorkshopName
            }]
          }]
        }
      };
      const res = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };
  // ── Derived state ─────────────────────────────────────────────────────────
  const morningWorkshops = workshops.filter(w => w.session === 'סדנת בוקר');
  const afternoonWorkshops = workshops.filter(w => w.session === 'סדנת צהריים');
  const selectedMorningWorkshop = workshops.find(w => w.id === form.morningWorkshopId);
  const selectedAfternoonWorkshop = workshops.find(w => w.id === form.afternoonWorkshopId);
  const hasWaitlist = (selectedMorningWorkshop?.isFull || selectedAfternoonWorkshop?.isFull) ?? false;
  const canSubmit = !submitting && emailStatus === 'valid' && form.fullName.trim() !== '' && form.email.trim() !== '' && form.phone.trim() !== '' && form.morningWorkshopId !== '' && form.afternoonWorkshopId !== '';
  // ── Success screen ────────────────────────────────────────────────────────
  if (submitted) {
    return <div dir="rtl" className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0a1628 0%, #0d2040 50%, #0a1628 100%)'
    }} data-id="element-3027" data-mp="3vzs0synh87xu">
        {showConfetti && <Confetti data-id="element-3028" data-mp="3mzwmwm2jdyu4" />}
        <div className="text-center px-6 relative z-10" data-id="element-3029" data-mp="3mybr6t4hk9uo">
          <div className="text-8xl mb-8" style={{
          animation: 'bounceIn 0.6s ease-out'
        }} data-id="element-3030" data-mp="3hzqrqvx25u7a">
            🎉
          </div>
          <h1 className="font-black mb-4 leading-tight" style={{
          fontFamily: "'Heebo', sans-serif",
          fontSize: 'clamp(2rem, 6vw, 4rem)',
          color: '#00d4ff',
          textShadow: '0 0 40px rgba(0,212,255,0.5)'
        }} data-id="element-3031" data-mp="3d8ihxyrtg6tl">
            ההרשמה שלך התקבלה! 🎉
          </h1>
          <p className="text-2xl font-light" style={{
          fontFamily: "'Heebo', sans-serif",
          color: 'rgba(255,255,255,0.8)'
        }} data-id="element-3032" data-mp="2z87pirauyk6b">
            נתראה בכנרת
          </p>
          {hasWaitlist && <p className="mt-4 text-base font-medium max-w-sm mx-auto leading-relaxed px-4 py-3 rounded-2xl" style={{
          fontFamily: "'Heebo', sans-serif",
          color: '#fbbf24',
          background: 'rgba(251,191,36,0.1)',
          border: '1px solid rgba(251,191,36,0.25)'
        }} data-id="element-3033" data-mp="2rh4m9437co4y">
              ⚠️ שים לב: סדנה אחת או יותר שבחרת מלאה — נשלח לך עדכון אם מקום
              יתפנה
            </p>}
          <div className="mt-8 flex justify-center gap-3" data-id="element-3034" data-mp="28jv5jup9hore">
            {['🌊', '🏄', '🚣', '💧', '🌊'].map((emoji, i) => <span key={i} className="text-3xl" style={{
            animationDelay: `${i * 0.1}s`
          }} data-id="element-3035" data-mp="24k2tv94kn2ht" data-mpi={i}>
                {emoji}
              </span>)}
          </div>
        </div>
        <style data-id="element-3036" data-mp="2dhd3kq1qqjkm">{`
          @keyframes bounceIn {
            0% { transform: scale(0); opacity: 0; }
            60% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); }
          }
        `}</style>
      </div>;
  }
  // ── Main form ─────────────────────────────────────────────────────────────
  return <div dir="rtl" className="min-h-screen relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #0a1628 0%, #0d2040 60%, #071020 100%)',
    fontFamily: "'Heebo', sans-serif"
  }} data-id="element-3037" data-mp="1fp5dqzq711ip">
      {/* Animated water background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-3038" data-mp="16pia079zrjmh">
        <div style={{
        position: 'absolute',
        top: '10%',
        right: '-20%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)',
        animation: 'pulse 8s ease-in-out infinite'
      }} data-id="element-3039" data-mp="1udjft8fscly7" />
        <div style={{
        position: 'absolute',
        bottom: '5%',
        left: '-10%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,153,204,0.08) 0%, transparent 70%)',
        animation: 'pulse 6s ease-in-out infinite reverse'
      }} data-id="element-3040" data-mp="1pm1ozd7h81qy" />
      </div>

      <style data-id="element-3041" data-mp="2rx9u1mte8dlq">{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800;900&display=swap');
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .aqua-input {
          background: rgba(255,255,255,0.05);
          border: 1.5px solid rgba(0,212,255,0.2);
          color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
        }
        .aqua-input:focus {
          border-color: #00d4ff;
          box-shadow: 0 0 0 3px rgba(0,212,255,0.15);
        }
        .aqua-input::placeholder { color: rgba(255,255,255,0.3); }
        .aqua-input option { background: #0d2040; color: white; }
        .aqua-input.error { border-color: #ff4d6d; box-shadow: 0 0 0 3px rgba(255,77,109,0.15); }
        .aqua-input.success { border-color: #00e676; box-shadow: 0 0 0 3px rgba(0,230,118,0.15); }
        .submit-btn {
          background: linear-gradient(135deg, #00d4ff, #0099cc);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0,212,255,0.4);
        }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .glass-card {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(0,212,255,0.12);
        }
      `}</style>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-start py-12 px-4" data-id="element-3042" data-mp="2uhy1nc701kk8">
        {/* Header */}
        <div className="text-center mb-10 w-full max-w-lg" data-id="element-3043" data-mp="152ndlc0e4xt0">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-3044" data-mp="1536jfucrnvqo">
            <span className="text-4xl" data-id="element-3045" data-mp="112d07ea9r544">🌊</span>
            <span className="text-4xl" data-id="element-3046" data-mp="10zid1ezvf670">🚣</span>
            <span className="text-4xl" data-id="element-3047" data-mp="1etrqm66ia8l7">🌊</span>
          </div>
          <h1 className="font-black mb-2 leading-tight" style={{
          fontSize: 'clamp(1.8rem, 5vw, 3rem)',
          color: '#00d4ff',
          textShadow: '0 0 30px rgba(0,212,255,0.4)',
          letterSpacing: '-0.02em'
        }} data-id="element-3048" data-mp="1a29rlno1mi2v">
            SupRace Kinneret 2026
          </h1>
          <p className="text-lg font-medium" style={{
          color: 'rgba(255,255,255,0.7)'
        }} data-id="element-3049" data-mp="1xrlxadk5w80f">
            הרשמה לסדנאות הפסטיבל
          </p>
          <div className="mt-4 h-px w-32 mx-auto" style={{
          background: 'linear-gradient(90deg, transparent, #00d4ff, transparent)'
        }} data-id="element-3050" data-mp="1stsqbib1b9z3" />
        </div>

        {/* Error banner */}
        {submitError && <div className="w-full max-w-lg mb-6 px-5 py-4 rounded-2xl text-right font-medium" style={{
        background: 'rgba(255,77,109,0.15)',
        border: '1px solid rgba(255,77,109,0.4)',
        color: '#ff8fa3'
      }} data-id="element-3051" data-mp="3ulp46ykocvx1">
            ⚠️ אירעה שגיאה בשליחת הטופס. אנא נסו שוב.
          </div>}

        {/* Form Card */}
        <div className="glass-card w-full max-w-lg rounded-3xl p-8 md:p-10" data-id="element-3052" data-mp="25b3l434fvk2s">
          {loadingWorkshops ? <div className="flex flex-col items-center justify-center py-16 gap-4" data-id="element-3053" data-mp="1rbc03ifi93fa">
              <div className="w-12 h-12 rounded-full border-4 border-t-transparent" style={{
            borderColor: 'rgba(0,212,255,0.3)',
            borderTopColor: '#00d4ff',
            animation: 'spin 0.8s linear infinite'
          }} data-id="element-3054" data-mp="1rdf10xi74gys" />
              <p style={{
            color: 'rgba(255,255,255,0.5)'
          }} data-id="element-3055" data-mp="1mbhadi7gujvt">
                טוען סדנאות...
              </p>
              <style data-id="element-3056" data-mp="3cp6nemyx30iu">{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div> : workshopError ? <div className="text-center py-12" data-id="element-3057" data-mp="3gph34rcih5aw">
              <p className="text-2xl mb-2" data-id="element-3058" data-mp="ticfgji6pro5">😕</p>
              <p style={{
            color: '#ff8fa3'
          }} data-id="element-3059" data-mp="tj4ant7vdndw">
                שגיאה בטעינת הסדנאות. אנא רעננו את הדף.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-3060" data-mp="kgmmvackgmos">
              {/* שם מלא */}
              <div data-id="element-3061" data-mp="fitjep2ne6s7">
                <label className="block text-sm font-semibold mb-2" style={{
              color: 'rgba(255,255,255,0.8)'
            }} data-id="element-3062" data-mp="fjcyly0wyh2j">
                  שם מלא{' '}
                  <span style={{
                color: '#00d4ff'
              }} data-id="element-3063" data-mp="18678y240o02a">
                    *
                  </span>
                </label>
                <input type="text" required placeholder="ישראל ישראלי" value={form.fullName} onChange={e => handleChange('fullName', e.target.value)} className="aqua-input w-full px-4 py-3 rounded-xl text-right" data-id="element-3064" data-mp="13dw6oslt026k" />
              </div>

              {/* אימייל */}
              <div data-id="element-3065" data-mp="1r4js8gon6qbl">
                <label className="block text-sm font-semibold mb-2" style={{
              color: 'rgba(255,255,255,0.8)'
            }} data-id="element-3066" data-mp="1r40d17qdmg19">
                  אימייל{' '}
                  <span style={{
                color: '#00d4ff'
              }} data-id="element-3067" data-mp="23f138js8l639">
                    *
                  </span>
                </label>
                <div className="relative" data-id="element-3068" data-mp="28h9xrkkrcg7h">
                  <input type="email" required placeholder="israel@example.com" value={form.email} onChange={e => handleChange('email', e.target.value)} onBlur={handleEmailBlur} className={`aqua-input w-full px-4 py-3 rounded-xl text-right ${emailStatus === 'invalid' ? 'error' : emailStatus === 'valid' ? 'success' : ''}`} dir="ltr" style={{
                textAlign: 'right'
              }} data-id="element-3069" data-mp="2cgr1o9kjd5aj" />
                  {emailStatus === 'checking' && <div className="absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-t-transparent" style={{
                borderColor: 'rgba(0,212,255,0.4)',
                borderTopColor: '#00d4ff',
                animation: 'spin 0.8s linear infinite'
              }} data-id="element-3070" data-mp="2w3yl4lsjd0vh" />}
                  {emailStatus === 'valid' && <div className="absolute top-1/2 left-3 -translate-y-1/2 text-lg" style={{
                color: '#00e676'
              }} data-id="element-3071" data-mp="3lh1u36qcs5t3">
                      ✓
                    </div>}
                </div>
                {emailStatus === 'invalid' && emailErrorMsg && <p className="mt-2 text-sm text-right leading-relaxed" style={{
              color: '#ff8fa3'
            }} data-id="element-3072" data-mp="3vfqzw14z5zsz">
                    {emailErrorMsg}
                  </p>}
              </div>

              {/* טלפון */}
              <div data-id="element-3073" data-mp="3hfg90y3o0l7t">
                <label className="block text-sm font-semibold mb-2" style={{
              color: 'rgba(255,255,255,0.8)'
            }} data-id="element-3074" data-mp="3hfyf0mg4qdsx">
                  טלפון{' '}
                  <span style={{
                color: '#00d4ff'
              }} data-id="element-3075" data-mp="3cmvhk388ek7g">
                    *
                  </span>
                </label>
                <input type="tel" required placeholder="050-0000000" value={form.phone} onChange={e => handleChange('phone', e.target.value)} className="aqua-input w-full px-4 py-3 rounded-xl text-right" dir="ltr" style={{
              textAlign: 'right'
            }} data-id="element-3076" data-mp="2jyzqbe1flki2" />
              </div>

              {/* Divider */}
              <div className="h-px w-full" style={{
            background: 'rgba(0,212,255,0.1)'
          }} data-id="element-3077" data-mp="plngum9xcmc7" />

              {/* סדנת בוקר */}
              <div data-id="element-3078" data-mp="1i8rg0amc66g2">
                <label className="block text-sm font-semibold mb-2" style={{
              color: 'rgba(255,255,255,0.8)'
            }} data-id="element-3079" data-mp="1i883xajdac9y">
                  🌅 סדנת בוקר{' '}
                  <span style={{
                color: '#00d4ff'
              }} data-id="element-3080" data-mp="19839s236padq">
                    *
                  </span>
                </label>
                <select required value={form.morningWorkshopId} onChange={e => handleWorkshopSelect('morning', e.target.value)} className="aqua-input w-full px-4 py-3 rounded-xl text-right appearance-none cursor-pointer" data-id="element-3081" data-mp="1d7ljs2tgw11s">
                  <option value="" data-id="element-3082" data-mp="1rwxzery982k">בחרו סדנת בוקר</option>
                  {morningWorkshops.map(w => <option key={w.id} value={w.id} data-id="element-3083" data-mp="306t1s6bxqm5u">
                      {w.isFull ? `${w.name} (רשימת המתנה)` : w.name}
                    </option>)}
                </select>
                {selectedMorningWorkshop?.isFull && <div className="mt-2 px-4 py-3 rounded-xl text-sm font-medium text-right" style={{
              background: 'rgba(251,191,36,0.1)',
              border: '1px solid rgba(251,191,36,0.3)',
              color: '#fbbf24'
            }} data-id="element-3084" data-mp="2qd3t54w47roc">
                    ⚠️ סדנה זו מלאה — תירשם לרשימת המתנה
                  </div>}
                {morningWorkshops.length === 0 && <p className="mt-1 text-xs" style={{
              color: 'rgba(255,255,255,0.4)'
            }} data-id="element-3085" data-mp="27lt5cwlorv0o">
                    אין סדנאות בוקר זמינות כרגע
                  </p>}
              </div>

              {/* סדנת צהריים */}
              <div data-id="element-3086" data-mp="22l5gagh0po93">
                <label className="block text-sm font-semibold mb-2" style={{
              color: 'rgba(255,255,255,0.8)'
            }} data-id="element-3087" data-mp="22lom4yte8m6r">
                  ☀️ סדנת צהריים{' '}
                  <span style={{
                color: '#00d4ff'
              }} data-id="element-3088" data-mp="1exrt0s7klwcm">
                    *
                  </span>
                </label>
                <select required value={form.afternoonWorkshopId} onChange={e => handleWorkshopSelect('afternoon', e.target.value)} className="aqua-input w-full px-4 py-3 rounded-xl text-right appearance-none cursor-pointer" data-id="element-3089" data-mp="1sycbfn9kpq5l">
                  <option value="" data-id="element-3090" data-mp="21w4qcqbduvsg">בחרו סדנת צהריים</option>
                  {afternoonWorkshops.map(w => <option key={w.id} value={w.id} data-id="element-3091" data-mp="1xwdvaaetpti4">
                      {w.isFull ? `${w.name} (רשימת המתנה)` : w.name}
                    </option>)}
                </select>
                {selectedAfternoonWorkshop?.isFull && <div className="mt-2 px-4 py-3 rounded-xl text-sm font-medium text-right" style={{
              background: 'rgba(251,191,36,0.1)',
              border: '1px solid rgba(251,191,36,0.3)',
              color: '#fbbf24'
            }} data-id="element-3092" data-mp="26rac9p4scagj">
                    ⚠️ סדנה זו מלאה — תירשם לרשימת המתנה
                  </div>}
                {afternoonWorkshops.length === 0 && <p className="mt-1 text-xs" style={{
              color: 'rgba(255,255,255,0.4)'
            }} data-id="element-3093" data-mp="2spy7au47ugn6">
                    אין סדנאות צהריים זמינות כרגע
                  </p>}
              </div>

              {/* Submit */}
              <button type="submit" disabled={!canSubmit} className="submit-btn w-full py-4 rounded-2xl font-black text-lg text-white mt-2" style={{
            fontFamily: "'Heebo', sans-serif",
            letterSpacing: '0.02em'
          }} data-id="element-3094" data-mp="32h159n1umfi8">
                {submitting ? <span className="flex items-center justify-center gap-3" data-id="element-3095" data-mp="3gfirthg0plyk">
                    <span className="w-5 h-5 rounded-full border-2 border-t-transparent inline-block" style={{
                borderColor: 'rgba(255,255,255,0.4)',
                borderTopColor: 'white',
                animation: 'spin 0.8s linear infinite'
              }} data-id="element-3096" data-mp="3lec3r5fnp4wm" />
                    שולח...
                  </span> : 'שלח הרשמה 🚣'}
              </button>

              <p className="text-center text-xs" style={{
            color: 'rgba(255,255,255,0.3)'
          }} data-id="element-3097" data-mp="1eq3h5xa69v6v">
                ההרשמה לסדנאות מיועדת לרוכשי כרטיסי הפסטיבל בלבד
              </p>
            </form>}
        </div>

        {/* Footer wave */}
        <div className="w-full max-w-lg mt-8 opacity-30" data-id="element-3098" data-mp="14t72taxleeue">
          <WaveDecoration data-id="element-3099" data-mp="22fxpq2ngkxlb" />
        </div>
      </div>
    </div>;
}